import { useGT } from 'gt-react';
import { WarningIcon } from '@phosphor-icons/react';
import { formatDateTime } from '@erp/shared-utils';
import type { BoardFreshnessRun } from '@erp/rpc';
import { cn } from '@/lib/utils';
import { lineRefreshStamp } from '../utils/lineRefreshStamp';
import { useMinuteClock } from './useMinuteClock';

/** The time alone when it is today, else month, day and time. */
function formatStampTime(iso: string, current: boolean): string {
  return new Date(iso).toLocaleString(
    'en-US',
    current
      ? { hour: 'numeric', minute: '2-digit' }
      : { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' },
  );
}

/** Both jobs with their full times, one per line. */
function stampTooltip(
  gt: ReturnType<typeof useGT>,
  { run, stock }: ReturnType<typeof lineRefreshStamp>,
  asOfSyncedAt: string | null,
  none: string,
): string {
  return [
    run.at === null
      ? gt('Replenishment engine run: none recorded', {
          context: 'line refresh stamp tooltip, no engine run',
        })
      : run.ended
        ? gt('Replenishment engine run, ended {time}', {
            context: 'line refresh stamp tooltip: when the engine run ended',
            time: formatDateTime(run.at),
          })
        : gt('Replenishment engine run, started {time} (no end recorded)', {
            context:
              'line refresh stamp tooltip: the engine run has a start time but no recorded end',
            time: formatDateTime(run.at),
          }),
    stock.asOf === null
      ? gt('Stock feed: no snapshot', {
          context: 'line refresh stamp tooltip, no stock snapshot',
        })
      : gt('Stock feed snapshot {date}, written {time}', {
          context:
            'line refresh stamp tooltip: stock snapshot date (YYYY-MM-DD) and when the feed wrote it',
          date: stock.asOf,
          time: asOfSyncedAt === null ? none : formatDateTime(asOfSyncedAt),
        }),
  ].join('\n');
}

/**
 * When the selected line was last refreshed (GEN-3285): the engine run that
 * produced it and the stock feed its position is read from. Either one not
 * from today turns the stamp amber with a warning glyph; the tooltip names
 * both jobs with their full times.
 */
export function LineRefreshStamp({
  engineRun,
  asOf,
  asOfSyncedAt,
  now,
}: {
  engineRun: BoardFreshnessRun | null | undefined;
  asOf: string | null;
  asOfSyncedAt: string | null;
  /** Fixed in tests; otherwise a clock that ticks past midnight on its own. */
  now?: Date;
}) {
  const gt = useGT();
  const clock = useMinuteClock();
  const stamp = lineRefreshStamp(
    { engineRun: engineRun ?? null, asOf, asOfSyncedAt },
    now ?? clock,
  );
  const { run, stock } = stamp;
  const none = gt('none', {
    context: 'line refresh stamp: no run or stock snapshot recorded',
  });
  const runText = run.at === null ? none : formatStampTime(run.at, run.current);
  const stockText =
    stock.syncedAt !== null
      ? formatStampTime(stock.syncedAt, stock.current)
      : (stock.asOf ?? none);
  const warn = (engineRun !== undefined && !run.current) || !stock.current;
  const tooltip = stampTooltip(gt, stamp, asOfSyncedAt, none);
  return (
    <span
      role="status"
      title={tooltip}
      className={cn(
        'flex shrink-0 items-center gap-1 whitespace-nowrap',
        warn
          ? 'font-medium text-amber-700 dark:text-amber-400'
          : 'text-muted-foreground',
      )}
    >
      {warn && <WarningIcon aria-hidden className="h-3.5 w-3.5" />}
      {gt('Engine run {run} · Stock {stock}', {
        context:
          'buying board line detail: when the engine run ended and when the stock was read',
        run: engineRun === undefined ? '…' : runText,
        stock: stockText,
      })}
    </span>
  );
}
